import React, { type ReactNode, useMemo } from 'react';

import { ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import type { ListItemProps } from '@mui/material/ListItem';
import ListItem from '@mui/material/ListItem';
import type { PopoverProps } from '@mui/material/Popover';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import TMXPopover from 'components/popovers/TMXPopover';
import { emptyObject } from 'components/utils';

import { useStyles } from './styles';

export interface ActionsMenuPropsInterface extends PopoverProps {
  id: string;
  children: ReactNode;
}

export const ActionsMenu: React.FC<React.PropsWithChildren<ActionsMenuPropsInterface>> = React.memo(
  ({ id, children, ...props }) => {
    const items = React.Children.toArray(children);
    const { t } = useTranslation();
    return useMemo(
      () => (
        <TMXPopover id={id} {...props}>
          {items.length ? (
            <List dense>
              {items.filter(Boolean).map((child: any, i) => (
                <ListItem
                  key={i}
                  disablePadding
                  className={cx(child?.props?.className)}
                  disabled={child?.props?.disabled}
                  onClick={child?.props?.onClick}
                >
                  {child}
                </ListItem>
              ))}
            </List>
          ) : (
            <Button disabled>{t('noAvailableActions')}</Button>
          )}
        </TMXPopover>
      ),
      [id, items, props, t],
    );
  },
);

export interface ActionsMenuItemPropsInterface {
  className?: any;
  icon?: React.ReactElement;
  style?: any;
  text: string | React.ReactNode;
  onClick?: ListItemProps['onClick'];
}

export const ActionsMenuItem: React.FC<React.PropsWithChildren<ActionsMenuItemPropsInterface>> = React.memo(
  ({ className, icon, text, style = emptyObject }) => {
    const { classes } = useStyles();
    return (
      <ListItemButton className={className} style={style}>
        {icon && (
          <ListItemIcon className={classes.actionIconWrapper}>
            {React.cloneElement(icon, { ...icon.props, className: cx(classes.actionIcon, icon.props.className) })}
          </ListItemIcon>
        )}
        <ListItemText>{text}</ListItemText>
      </ListItemButton>
    );
  },
);
